import { Box, Text, VStack } from '@chakra-ui/react';
import { SubmitButton } from '@pluxee-design-system/react-hook-form';
import VideoButton from 'common/buttons/VideoButton';
import { CompanyIdControl } from 'common/forms';
import { SplitScreen } from 'common/layouts';
import { TextTitle } from 'common/typography';
import CampaignLogo from 'components/Affiliation/CampaignLogo';
import { WizardStep } from 'components/WizardForm/types';
import { companyIdAsync, createSchema, MAGIC_ICO_BE } from 'config';
import useTranslations from 'i18n';
import { debounce } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import gtm from 'trackers/gtm';
import { PageVideoEnum } from 'types/videos';
import { parseHouseNumbers } from 'utils/getHouseNumber';
import maskString from 'utils/maskString';
import scrollToTop from 'utils/scrollToTop';
import { ValidationError } from 'yup';
import { PrefillStatusEnum } from '../prefillStatus';
import { AffiliationValues, ExtraWizardProps } from '../types';

const trackCompanyId = debounce(
  (companyId?: string) => companyId && gtm.setVariables({ site_company_id: companyId.trim() }),
  500,
);

export const CompanyIdStep: WizardStep<ExtraWizardProps, AffiliationValues> = ({
  campaign,
  config: {
    companyId: { form: config },
  },
  isSubmitClicked,
}) => {
  const { t } = useTranslations();
  const { watch } = useFormContext<AffiliationValues>();
  const valuesCompanyId = watch('companyId');

  const trackSubmit = useCallback(() => {
    if (isSubmitClicked) {
      isSubmitClicked.current = true;
    }
    scrollToTop();
  }, [isSubmitClicked]);

  useEffect(() => {
    trackCompanyId(valuesCompanyId);
  }, [valuesCompanyId]);

  return (
    <SplitScreen>
      {campaign && <CampaignLogo campaign={campaign} />}
      <VideoButton page={PageVideoEnum.AffiliationCompanyId} />
      <TextTitle
        mb={6}
        title={t('company_id.pageHeader', 'We made affiliation super easy!')}
        subtitle={t(
          'company_id.subheader',
          'More than 450 000 affiliated already joined us, and you?',
        )}
      />
      <VStack align="stretch" spacing={10}>
        <Box>
          <Text as="h3" variant="title.desktopCard">
            {t('company_id.sectionTexts.mainTitle', 'What is your Identification Number?')}
          </Text>
          <Text variant="subtitle.card" color="semantic.text.tertiary">
            {t(
              'company_id.sectionTexts.description',
              'We need this information to identify your company and prefill your information.',
            )}
          </Text>
        </Box>
        <CompanyIdControl
          mandatory
          name="companyId"
          id="CompanyIdStep_companyId"
          placeholder={t('company_id.form.companyId.placeholder', '12345678')}
          title={t('company_id.form.companyId.label', 'Your company ID')}
          format={config.companyId.format}
        />
        <SubmitButton
          variant="primaryFilled"
          id="CompanyIdStep__validateButton"
          onClick={trackSubmit}
          width="100%"
        >
          {t('company_id.button.validate', 'Confirm')}
        </SubmitButton>
      </VStack>
    </SplitScreen>
  );
};

CompanyIdStep.initial = () => ({
  companyId: '',
  masterId: '',
});

CompanyIdStep.validate =
  ({ config, isSubmitClicked, t }) =>
  async (values) => {
    let errors: ValidationError[] = [];
    try {
      const syncSchema = createSchema<AffiliationValues>(config.companyId.form);
      await syncSchema.validateSync(values);
    } catch (error) {
      errors = [error];
    }

    // @ts-expect-error: companyIdError is not typed correctly
    const companyIdError = errors.find((e) => e.path === 'companyId')?.message?.props;
    if (isSubmitClicked?.current && companyIdError?.props) {
      gtm.trackClickFindMe(
        companyIdError?.id,
        t!(companyIdError?.id, companyIdError?.defaultMessage),
      );
      isSubmitClicked.current = false;
    }

    return errors;
  };

CompanyIdStep.validateSubmit = async (values, { callCache, t }) => {
  let errors: ValidationError[] = [];
  try {
    const asyncSchema = companyIdAsync(callCache, true);
    await asyncSchema.validate(values);
  } catch (error) {
    errors = [error];
  }

  // @ts-expect-error: companyIdError is not typed correctly
  const companyIdError = errors.find((e) => e.path === 'companyId')?.message?.props;
  if (companyIdError) {
    gtm.trackClickFindMe(
      companyIdError?.id,
      t!(companyIdError?.id, companyIdError?.defaultMessage),
    );
  } else {
    gtm.trackClickFindMe();
  }

  return errors;
};

CompanyIdStep.onSubmit = async (
  currentValues,
  setNextStepProps,
  {
    callCache,
    config: {
      companyInfo: { form: config },
      paymentInfo: { form: paymentInfoConfig },
    },
    freshValues,
  },
) => {
  let values = currentValues;

  // freshValues contains empty initialVales enriched by a companyId stored in a localStore
  // in case companyId is different from stored, we've to clear it for next upcoming changes
  if (currentValues.companyId !== freshValues!.companyId) {
    freshValues!.companyId = '';
    values = freshValues!;
  }

  const companyId = currentValues.companyId;
  const response = callCache[companyId];

  const masterId = response?.masterId || '';
  const companyDetails = response?.entry;
  const prefillDataStatus = companyDetails
    ? PrefillStatusEnum.Prefilled
    : PrefillStatusEnum.NotPrefilled;

  if (companyDetails && prefillDataStatus === PrefillStatusEnum.Prefilled) {
    const companyInfo: Partial<AffiliationValues> = {
      companyId,
      masterId,
      companyName: companyDetails.companyName || '',
      commercialName: values.commercialName
        ? values.commercialName
        : companyDetails.companyNameCommercial || companyDetails.companyName || '',
      streetName: companyDetails.street || companyDetails.city || '',
      ...parseHouseNumbers(
        companyDetails.houseNumber,
        undefined, // not returned by FindMe
        config.orientationNumber?.visible,
      ),
      city: companyDetails.city || '',
      box: companyDetails.box || '',
      postalCode: maskString(companyDetails.zip || '', config.postalCode.format),
      vatNumber: companyDetails.vatId || '',
      vatPayer: Boolean(companyDetails.vatId && companyDetails.vatId.length > 0),
      courtFileNumber: companyDetails.courtFileNumber || '',
      legalForm: companyDetails.legalForm || '',
      accountNumber: values.accountNumber ? values.accountNumber : companyDetails.accountIban || '',
    };
    const addressKeys = ['streetName', 'houseNumber', 'city', 'postalCode'] as Array<
      keyof typeof config
    >;
    const prefillAddressIncomplete = addressKeys.some(
      (f) => config[f].visible && config[f].mandatory && !companyInfo[f],
    );
    const allowedCompanyServices = companyDetails.benefitCategories ?? [];

    if (values.country === 'be') {
      companyInfo.accountNumber = companyInfo.accountNumber?.replace(
        paymentInfoConfig.accountNumber.format || '',
        '',
      );
    }

    if (values.country === 'be' && prefillAddressIncomplete) {
      Object.assign(companyInfo, {
        streetName: values.streetName || companyInfo.streetName || '',
        houseNumber: values.houseNumber || companyInfo.houseNumber || '',
        city: values.city || companyInfo.city || '',
        box: values.box || companyInfo.box || '',
        postalCode: maskString(
          values.postalCode || companyInfo.postalCode || '',
          config.postalCode.format,
        ),
      });
    }

    setNextStepProps({ allowedCompanyServices, prefillDataStatus, prefillAddressIncomplete });
    return { ...values, ...companyInfo }; // set values for a next step
  } else {
    setNextStepProps({ prefillDataStatus });
  }

  // TODO: remove mock data
  if (companyId === MAGIC_ICO_BE) {
    const companyInfo: Partial<AffiliationValues> = {
      companyId,
      masterId,
      companyName: 'BELGI',
      commercialName: 'BELGI',
      streetName: 'Kozenstraat',
      houseNumber: '31',
      city: 'Hasselt',
      postalCode: maskString('3512', config.postalCode.format),
      legalForm: 'INPASS',
      // activity: [],

      // position: '41',
      // language: 'en',
      accountNumber: '71096123456769',
      invoiceEmail: 'testan.testovicz@gmail.com',
      poReferenceNumber: '123REF',
      gdpr: true,
      termsAndConditions: true,
    };
    return { ...values, ...companyInfo }; // set values for a next step
  }

  return { ...values, companyId, masterId }; // reset company step
};

export default CompanyIdStep;
